/**
 * 席順の補正
 *
 * docs/01-ゲームデザイン.md §4
 *
 * 双六は先手から動くので、**空白の国を先に取れる先手が有利**になる。
 * 盤上の国は有限（28）で、序盤の数ターンで大半が埋まるため、
 * この差は最後まで残る。
 *
 * AI 同士 200局×3回の実測で、補正なしの勝率は
 *   1番手 34.0 / 26.0 / 33.0 %
 *   4番手 14.5 / 15.5 / 15.0 %
 * と、seed を変えても4番手だけが一貫して10ポイント低かった。
 * （n=100 では 11pt に見えたが、それは標本が足りなかっただけ）
 *
 * 600局で測り直すと、補正なしは
 *   1番手 39.2 / 2番手 34.8 / 3番手 19.5 / 4番手 6.5 %
 * で、素の差は当時よりさらに開いていた。
 *
 * 後手ほど手厚くして埋める。兵だけで埋めると4番手が逆に勝ちすぎる
 * （実測 31.5%）ので、石高と兵を混ぜている。
 */

import type { Effect } from '@tenka/core';

const gain = (res: 'koku' | 'hei', n: number): Effect => ({ t: 'gain', res, n });

/**
 * 座席番号ごとの開始時の効果。
 *
 * 【2026-08-11 に測り直して増やした】
 * 「19.5pt → 7.0pt に縮んだ」と書いてあったが、**それは古い盤面の話**だった。
 * 600局×3系列で測ると、補正ありで 21.0pt。補正を空にして測ると 32.7pt。
 * 補正は効いていたが、**その後の変更で素の差のほうが大きくなっていて、
 * 補正だけが据え置かれていた**。効いているかどうかは、
 * 外して測ってみるまで分からない。
 *
 * 増やした結果（600局×3系列、1系列あたり n=600）：
 *   1番手 31.0 / 2番手 26.0 / 3番手 21.6 / 4番手 21.4 （開き 9.6pt）
 * 補正前の 35.3 / 29.0 / 21.3 / 14.3（開き 21pt）から半分以下になった。
 *
 * 【ここから先は石高と兵では埋まらない】
 * さらに 1.5 倍（`koku3` / `koku5+hei3` / `koku6+hei5`）にして
 * 3系列を測っても開きは 10.0pt で、**変わらなかった**。
 * 1番手の利は「空白の国を先に選べる」という手番そのものの利なので、
 * 手持ちの資源を足しても、ある所から先は埋まらない。
 * 小さいほうの補正を採る — 同じ結果なら、開始の持ち物を歪めないほうがよい。
 *
 * 残っているのは 1番手だけである（2〜4番手は 21.4〜26.0 に収まった）。
 * 目標帯の読み方については docs/04「席順の開きをどう読むか」を見ること。
 */
export const SEAT_BONUS: readonly (readonly Effect[])[] = [
  [], // 1番手：補正なし
  [gain('koku', 2)],
  [gain('koku', 4), gain('hei', 2)],
  [gain('koku', 5), gain('hei', 4)],
];
